/**
 * src/components/landing/ModalLimite.tsx
 * =============================================================================
 * Lo que se le enseña a alguien cuando no ha podido subir.
 *
 * POR QUÉ UN MODAL Y NO UN TEXTO ROJO PEQUEÑO
 * -------------------------------------------
 * Porque no es un error de validación, es un tope alcanzado: la persona no ha
 * hecho nada mal y no puede arreglarlo escribiendo otra cosa. Necesita entender
 * qué pasa y qué opciones tiene, y eso no cabe en una línea al pie.
 *
 * Hasta ahora estos casos caían en el mensaje genérico —"algo falló, inténtalo
 * de nuevo"—, que es lo peor que se puede decir aquí: invita a reintentar algo
 * que no va a funcionar nunca, y deja a la persona pensando que el producto
 * está roto en vez de que ha llegado a un límite.
 *
 * TRES CASOS, TRES MENSAJES
 * -------------------------
 *   free_tier_paused       hemos parado el plan gratuito (decisión nuestra)
 *   storage_quota_exceeded ha llenado su espacio (situación suya)
 *   signup_paused          no aceptamos cuentas nuevas ahora mismo
 *
 * SIEMPRE SE OFRECE PRO. Decisión del dueño el 2026-08-23, y tiene sentido:
 * si las subidas gratuitas están paradas, Pro es exactamente la salida que esa
 * persona necesita para seguir trabajando. Decirle solo "vuelve más tarde" la
 * deja atascada sin darle ninguna opción.
 *
 * Lo que sí cambia según el caso es el TONO: cuando el freno lo hemos puesto
 * nosotros se dice que es temporal y que a quien paga no le afecta; cuando se
 * ha quedado sin espacio se dice cuánto ocupaba su plan.
 *
 * A los usuarios de pago esto no les aparece nunca — el servidor ni siquiera
 * mira el freno cuando el plan no es gratuito. Comprobado en
 * `cloudflare/src/lib/freno.integracion.test.ts`.
 * =============================================================================
 */

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Info, X } from 'lucide-react'
import type { MotivoLimite } from '@/lib/motivosLimite'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { formatBytes } from '@/services/billingService'

/**
 * Tope del plan gratuito (300 MB, espejo de `FREE_BYTES` en
 * `backend/src/lib/storage-quota.ts`). Un límite por encima de esto solo
 * puede ser de un espacio de pago (Pro o compartido), y a esa persona no se
 * le puede decir "tu plan gratuito" ni ofrecerle "pásate a Pro".
 */
const BYTES_PLAN_GRATUITO = 300 * 1024 * 1024


interface Props {
  motivo: MotivoLimite | null
  onClose: () => void
  /**
   * Límite del espacio en bytes, tal como lo manda el servidor. Solo se usa
   * si se quedó sin espacio. Sin él se asume el plan gratuito, que es el
   * caso con diferencia más frecuente.
   */
  limiteBytes?: number
}

export function ModalLimite({ motivo, onClose, limiteBytes }: Props) {
  const { t, i18n } = useTranslation()
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, motivo !== null)

  useEffect(() => {
    if (!motivo) return undefined
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [motivo, onClose])

  if (!motivo) return null

  const lleno = motivo === 'storage_quota_exceeded'
  const registro = motivo === 'signup_paused'

  const titulo = lleno
    ? t('limite.llenoTitulo')
    : registro
      ? t('limite.registroTitulo')
      : t('limite.pausadoTitulo')

  // Espacio de pago lleno: mismo título, pero sin hablar de "plan gratuito"
  // ni prometer los 50 GB de Pro a quien ya los tiene.
  const bytes = limiteBytes ?? BYTES_PLAN_GRATUITO
  const limite = formatBytes(bytes, i18n.resolvedLanguage)
  const texto = lleno
    ? bytes > BYTES_PLAN_GRATUITO
      ? t('limite.llenoTextoPago', { limite })
      : t('limite.llenoTexto', { limite })
    : registro
      ? t('limite.registroTexto')
      : t('limite.pausadoTexto')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-lg p-6 space-y-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2.5 min-w-0">
            <Info className="h-5 w-5 text-zinc-700 shrink-0 mt-0.5" strokeWidth={1.8} />
            <h2 className="text-base font-semibold text-zinc-900">{titulo}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('visor.herramientas.cerrar')}
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <p className="text-sm text-zinc-600 leading-relaxed">{texto}</p>

        {/* La coletilla que evita el susto: quien paga no está afectado. Solo
            aparece cuando el freno lo hemos puesto nosotros. */}
        {!lleno && !registro && (
          <p className="text-xs text-zinc-500">{t('limite.pausadoPro')}</p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-md text-sm text-zinc-700 hover:bg-zinc-100"
          >
            {t('limite.entendido')}
          </button>
          {/* Se ofrece Pro en los tres casos: es la salida real para quien
              quiere seguir trabajando ahora. */}
          <Link
            to="/precios"
            className="h-9 px-4 inline-flex items-center rounded-md bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800"
          >
            {t('limite.verPlanes')}
          </Link>
        </div>
      </div>
    </div>
  )
}
